import React from 'react';
import _ from 'lodash';

import MPWApi from 'API/MPWApi';
import {
  success,
  loading,
  handleErrorException,
  cancelRequest,
} from 'State/actions';
import { CANCEL_REQUEST } from 'State/types';
import { isRequestCancelation } from 'Common/Helpers';
import { errorToast, successToast } from 'Common/ToastHelper';
import { useMergeState } from 'Common/Hooks/useMergeState';
import { HTTP_METHODS } from 'Common/Constants';

const types = {
  API_LOADING: 'API_LOADING',
  API_SUCCESS: 'API_SUCCESS',
  API_ERROR: 'API_ERROR',
  CANCEL_REQUEST,
};

const INITIAL_STATE = {
  loading: false,
  error: null,
  data: null,
  source: null,
  success: false,
};

const createInitialState = (initialData) => ({
  ...INITIAL_STATE,
  data: initialData,
});

const apiReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case types.CANCEL_REQUEST:
      if (action.cancelable && state.source) {
        state.source.cancel();
      }
      return {
        ...state,
        source: null,
        loading: false,
        error: null,
      };
    case types.API_LOADING:
      if (action.cancelable && action.payload) {
        if (state.source) {
          state.source.cancel();
        }
        return { ...state, loading: action.payload, source: action.source };
      }
      return {
        ...state,
        loading: action.payload,
        source: null,
        success: false,
      };
    case types.API_SUCCESS:
      return {
        ...state,
        data: action.payload,
        success: true,
        source: null,
      };
    case types.API_ERROR:
      return {
        ...state,
        error: action.payload,
        source: null,
      };
    default:
      return state;
  }
};

const methodMap = {
  [HTTP_METHODS.POST]: 'postGenericInsertUpdate',
  [HTTP_METHODS.GET]: 'getGeneric',
  DEFAULT: 'postGenericInsertUpdate',
};

/**
 * Converts standard http methods to MPWApi class methods
 * @param {String} method
 */
const methodToMPWApiMethod = (method) =>
  _.get(methodMap, [_.toUpper(method)]) || methodMap['DEFAULT'];

const defaultOpts = {
  Api: MPWApi,
  method: HTTP_METHODS.POST,
  resource: '',
  data: {},
  cancelable: true,
  showErrorToast: true,
  showSuccessToast: false,
  successToastMessage: '',
};
/**
 * Hook to request data from an API
 * @param {Object} initialData
 * @param {Object} opts
 * @param {Object} opts.Api - pass in own Api instance
 * @param {String} opts.resource - api resource
 * @param {Object} opts.data
 * @param {String} opts.method - MPWApi method to use
 * @param {Boolean} opts.showErrorToast - should toast(s) appear when there is an api error
 * @param {Boolean} opts.showSuccessToast - should a toast appear when the api is successful
 *
 * @example
 *
 * const [{ loading, data }, { request }] = useApi([])
 * const [params, setParams] = React.useState({ ViewType: 0 })
 *
 * useEffect(() => {
 *    request('Ingredient/DiscontinuedIngredients', params)
 *    // or
 *    request.post('Ingredient/DiscontinuedIngredients', params)
 * }, [params])
 */
const useApi = (initialData, opts) => {
  const options = { ...defaultOpts, ...opts };

  const {
    Api,
    resource,
    data,
    method,
    cancelable,
    showErrorToast,
    showSuccessToast,
  } = options;

  const [apiParams, mergeApiParams] = useMergeState({ method, resource, data });

  const setData = (data) => mergeApiParams({ data });
  const setResource = (resource) => mergeApiParams({ resource });
  const request = (resource, data) => {
    mergeApiParams({ resource, data });
  };
  const requestWithMethod = (method, resource, data) => {
    mergeApiParams({ method, resource, data });
  };

  const initialApiState = createInitialState(initialData);
  const [apiState, dispatch] = React.useReducer(apiReducer, initialApiState);

  const cancel = () => dispatch(cancelRequest());

  /**
   * Displays error toast(s) for an api error
   */
  React.useEffect(() => {
    if (showErrorToast && apiState.error) {
      errorToast(apiState, {}, 'error');
    }
  }, [apiState.error]);

  /**
   * Displays a success toast for api success
   */
  React.useEffect(() => {
    if (showSuccessToast && apiState.success) {
      successToast(apiState, {}, 'success', options.successToastMessage);
    }
  }, [apiState.success]);

  /**
   * Makes the request when the resource/data changes
   */
  React.useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        if (!apiParams.resource) {
          return;
        }

        let API = Api;

        const loadingArgs = [types.API_LOADING, true];
        // if cancelable, create a cancelable api and add to the loading action
        if (cancelable) {
          const { Api: cancelableApi, source } = Api.createCancelableApi();
          API = cancelableApi;
          loadingArgs.push(cancelable, source);
        }
        const apiArgs = [apiParams.resource, apiParams.data];

        dispatch(loading(...loadingArgs));

        // makes the request
        const mpwApiMethod = methodToMPWApiMethod(apiParams.method);
        const response = await API[mpwApiMethod](...apiArgs);

        if (mounted) {
          dispatch(success(types.API_SUCCESS, response.data.Data));
          dispatch(loading(types.API_LOADING, false));
        }
      } catch (ex) {
        if (mounted) {
          dispatch(handleErrorException(ex, types.API_ERROR));
          if (!isRequestCancelation(ex)) {
            dispatch(loading(types.API_LOADING, false));
          }
        }
      }
    };
    fetchData();
    return () => {
      // cancels pending request
      // marks the hook as unmounted to prevent an unmounted component from updating its internal state
      mounted = false;
      if (cancelable) {
        cancel();
      }
    };
  }, [apiParams.resource, apiParams.data, apiParams.method]);

  /**
   * @param {Object} obj
   * @param {Function} obj.request - function to update the request body/resource
   * @param {Function} obj.setResource - function to update the request resource
   * @param {Function} obj.setData - function to update the request body
   * @param {Object} obj.reqData - contains the current request body
   * @param {String} obj.reqResource - contains the current request resource
   * @param {Function} obj.cancel - function to manually cancel request
   */

  /**
   * Creates request.get, request.post, etc
   */
  Object.keys(methodMap).forEach((method) => {
    const requestForMethod = (...args) => requestWithMethod(method, ...args);
    request[_.toLower(method)] = requestForMethod;
    request[method] = requestForMethod;
  });

  const returnParams = {
    request,
    setResource,
    setData,
    reqData: apiParams.data,
    reqResource: apiParams.resource,
    reqMethod: apiParams.method,
    cancel,
  };

  return [apiState, returnParams];
};

export default useApi;
export { useApi };



// WEBPACK FOOTER //
// ./src/common/Hooks/useApi.js